import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getSoilByExploitationAndPointNumber } from '../../../Utils/informations/soil';
import { getIrrigationByExploitationAndDate } from '../../../Utils/informations/irrigation';
import { getPointsByExploitationAndValve, getPointAudits, getPointInfo } from '../../../Utils/exploitations/points';
import { getValvesByExploitation } from '../../../Utils/exploitations/valves';
import { useParams } from 'react-router-dom';
import { Typography, Alert } from 'antd';
import Loading from '../../Shared/Loading';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import ParameterSelection from './ParameterSelection';
import IrrigationChartSection from './IrrigationChartSection';
import FertilizationChartSection from './FertilizationChartSection';


dayjs.extend(isBetween);
const { Title } = Typography;


const SoilIrrigationMetricsGraphs = () => {
  const [soilData, setSoilData] = useState([]);
  const [irrigationData, setIrrigationData] = useState([]);
  const [exploitationPoints, setExploitationPoints] = useState([]);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState([dayjs().subtract(7, 'days'), dayjs()]);
  const [pointAudits, setPointAudits] = useState([]);
  const [dateRangeOptions, setDateRangeOptions] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const { exploitationId } = useParams();
  const [valves, setValves] = useState([]);
  const [selectedValve, setSelectedValve] = useState(null);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [pointInfo, setPointInfo] = useState(null);

  const filterIrrigationData = useCallback((data, selectedValve) => {
    return data.filter(item => {
      const valveNumbers = [];
      for (let i = 1; i <= 12; i++) {
        if (Number(item[`v${i}`]) === 1) valveNumbers.push(i);
      }
      if (valveNumbers.length === 0 || !selectedValve) return false;
      return valveNumbers.includes(selectedValve.number);
    });
  }, []);
  

  useEffect(() => {
    if (selectedPoint) {
      const fetchPointInfo = async () => {
        try {
          const info = await getPointInfo(selectedPoint.point_id);
          setPointInfo(info.data);
        } catch (error) {
          console.error('Erreur lors de la récupération des informations du point:', error);
          setPointInfo(null);
        }
      };

      fetchPointInfo();
    }
  }, [selectedPoint]);


  const processIrrigationData = useCallback((filteredData, valvesResults, selectedValve) => {
    return filteredData.map(item => {
      const valveNumbers = [];
      for (let i = 1; i <= 12; i++) {
        if (Number(item[`v${i}`]) === 1) valveNumbers.push(i);
      }
      const totalFlow = valveNumbers.reduce((sum, num) => {
        const valve = valvesResults.find(v => v.number === num);
        if (!valve) return sum;
        return sum + (Number(valve.pluviometry) * valve.valve_superficie_net);
      }, 0);
      const valveFlow = Number(selectedValve.pluviometry) * selectedValve.valve_superficie_net;
      const percentage = totalFlow > 0 ? valveFlow / totalFlow : 0;
      const hectares = selectedValve.valve_superficie_net;
  
      return {
        ...item,
        volume: (percentage * item.volume) / hectares,
        volume_acide: (percentage * item.volume_acide) / hectares,
        volume_engrais_1: (percentage * item.volume_engrais_1) / hectares,
        volume_engrais_2: (percentage * item.volume_engrais_2) / hectares,
        volume_engrais_3: (percentage * item.volume_engrais_3) / hectares,
        volume_engrais_4: (percentage * item.volume_engrais_4) / hectares,
        volume_engrais_5: (percentage * item.volume_engrais_5) / hectares,
      };
    });
  }, []);


  
  const fetchInitialData = useCallback(async () => {
    if (!exploitationId) return;

    setLoading(true);
    setError(null);

    try {
      const [valvesResults, pointsResults] = await Promise.all([
        getValvesByExploitation(exploitationId),
        getPointsByExploitationAndValve(exploitationId)
      ]);

      setValves(valvesResults);
      setExploitationPoints(pointsResults.data);

      if (pointsResults.data.length > 0) {
        const initialPoint = pointsResults.data[0];
        setSelectedPoint(initialPoint);
        const associatedValve = valvesResults.find(valve => valve.valve_id === initialPoint.valve_id);
        setSelectedValve(associatedValve);
        await fetchPointAudits(initialPoint.point_id);
      }

      setIsInitialDataLoaded(true);
    } catch (err) {
      setError('Erreur lors du chargement des données initiales');
      console.error('Error fetching initial data:', err);
    } finally {
      setLoading(false);
    }
  }, [exploitationId]);

  const fetchSoilAndIrrigationData = useCallback(async () => {
    if (!selectedDateRange || !selectedValve || !selectedPoint) return;

    setLoading(true);
    setError(null);

    try {
      let [start, end] = selectedDateRange.split(',');
      start = dayjs(start).format('YYYY-MM-DD');
      end = dayjs(end).format('YYYY-MM-DD');
      const [irrigationResults, soilResults] = await Promise.all([
        getIrrigationByExploitationAndDate(exploitationId, start, end),
        getSoilByExploitationAndPointNumber(exploitationId, selectedPoint.point_number, start, end)
      ]);

      const filteredIrrigationData = filterIrrigationData(irrigationResults, selectedValve);
      const processedIrrigationData = processIrrigationData(filteredIrrigationData, valves, selectedValve);

      setIrrigationData(processedIrrigationData);
      setSoilData(soilResults);
    } catch (err) {
      setError(err.response?.status === 404
        ? 'Pas de données pour cette exploitation sur cette période'
        : 'Erreur lors du chargement des données');
      console.error('Error fetching soil and irrigation data:', err);
    } finally {
      setLoading(false);
    }
  }, [exploitationId, selectedDateRange, selectedValve, selectedPoint, filterIrrigationData, processIrrigationData, valves]);

  const fetchPointAudits = useCallback(async (pointId) => {
    try {
      const audits = await getPointAudits(pointId);
      setPointAudits(audits);
    } catch (error) {
      console.error('Erreur lors de la récupération des audits du point:', error);
      setPointAudits([]);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    if (isInitialDataLoaded && selectedPoint && selectedValve) {
      const start = dateRange[0].format('YYYY-MM-DD');
      const end = dateRange[1].format('YYYY-MM-DD');
      setSelectedDateRange(`${start},${end}`);
    }
  }, [isInitialDataLoaded, selectedPoint, selectedValve, dateRange]);

  useEffect(() => {
    if (isInitialDataLoaded && selectedDateRange && selectedValve && selectedPoint) {
      fetchSoilAndIrrigationData();
    }
  }, [isInitialDataLoaded, selectedDateRange, selectedValve, selectedPoint, fetchSoilAndIrrigationData]);

  useEffect(() => {
    if (dateRange && dateRange.length === 2 && pointAudits.length > 0) {
      generateDateRangeOptions();
    } else if (dateRange && dateRange.length === 2) {
      const start = dateRange[0].format('YYYY-MM-DD');
      const end = dateRange[1].format('YYYY-MM-DD');
      setDateRangeOptions([{
        label: `${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`,
        value: `${start},${end}`,
        valve: selectedPoint?.valve_id || null
      }]);
      setSelectedDateRange(`${start},${end}`);
    }
  }, [pointAudits, dateRange, selectedPoint]);

  useEffect(() => {
    if (selectedDateRange) {
      pointAudits.map(audit => {
        if (dayjs(audit.changed_at).isBetween(dayjs(selectedDateRange.split(',')[0]), dayjs(selectedDateRange.split(',')[1]), null, '[]')) {
          if (audit.changes && audit.changes.valve_id && audit.changes.valve_id.old) {
            const associatedValve = valves.find(valve => valve.valve_id === Number(audit.changes.valve_id.old));
            if (associatedValve){
              setSelectedValve(associatedValve);
            }

          }
        }
      });
    }
  }, [selectedDateRange]);

  const generateDateRangeOptions = useCallback(() => {
    if (!dateRange || dateRange.length !== 2) return;
  
    const options = [];
    const start = dayjs(dateRange[0].format('YYYY-MM-DD'));
    const end = dayjs(dateRange[1].format('YYYY-MM-DD'));
  
    const formatDateOrDateTime = (date1, date2) => {
      if (date1.isSame(date2, 'day')) {
        return `${date1.format('DD/MM/YYYY HH:mm')} - ${date2.format('HH:mm')}`;
      }
      return `${date1.format('DD/MM/YYYY')} - ${date2.format('DD/MM/YYYY')}`;
    };
  
    const formatValueDateOrDateTime = (date1, date2) => {
      if (date1.isSame(date2, 'day')) {
        return `${date1.format('YYYY-MM-DD HH:mm')},${date2.format('YYYY-MM-DD HH:mm')}`;
      }
      return `${date1.format('YYYY-MM-DD')},${date2.format('YYYY-MM-DD')}`;
    };
  
    const coordinateChanges = pointAudits.filter(audit =>
      audit.changes && audit.changes.coordinates &&
      dayjs(audit.changed_at).isBetween(start, end, null, '[]')
    ).sort((a, b) => dayjs(a.changed_at).diff(dayjs(b.changed_at)));
  
    if (coordinateChanges.length > 0) {
      options.push({
        label: formatDateOrDateTime(start, dayjs(coordinateChanges[0].changed_at)),
        value: formatValueDateOrDateTime(start, dayjs(coordinateChanges[0].changed_at)),
        valve: coordinateChanges[0].changes.valve_id ? coordinateChanges[0].changes.valve_id.old : null,
        valve_name: coordinateChanges[0].changes.valve_name ? coordinateChanges[0].changes.valve_name.old : null
      });
  
      for (let i = 0; i < coordinateChanges.length - 1; i++) {
        const currentDate = dayjs(coordinateChanges[i].changed_at);
        const nextDate = dayjs(coordinateChanges[i + 1].changed_at);
        options.push({
          label: formatDateOrDateTime(currentDate, nextDate),
          value: formatValueDateOrDateTime(currentDate, nextDate),
          valve: coordinateChanges[i].changes.valve_id ? coordinateChanges[i].changes.valve_id.old : null,
          valve_name: coordinateChanges[i].changes.valve_name ? coordinateChanges[i].changes.valve_name.old : null
        });
      }
  
      options.push({
        label: formatDateOrDateTime(dayjs(coordinateChanges[coordinateChanges.length - 1].changed_at), end),
        value: formatValueDateOrDateTime(dayjs(coordinateChanges[coordinateChanges.length - 1].changed_at), end),
        valve: coordinateChanges[coordinateChanges.length - 1].changes.valve_id ? coordinateChanges[coordinateChanges.length - 1].changes.valve_id.old : null,
        valve_name: coordinateChanges[coordinateChanges.length - 1].changes.valve_name ? coordinateChanges[coordinateChanges.length - 1].changes.valve_name.old : null
      });
    } else {
      options.push({
        label: formatDateOrDateTime(start, end),
        value: formatValueDateOrDateTime(start, end),
        valve: selectedPoint?.valve_id || null
      });
    }
  
    // remove duplicates
    const uniqueOptions = options.filter((option, index, self) =>
      index === self.findIndex(t => (
        t.label === option.label && t.value === option.value
      ))
    );
  
    setDateRangeOptions(uniqueOptions);
    setSelectedDateRange(uniqueOptions[0].value);
  }, [dateRange, pointAudits, selectedPoint]);
  const handlePointChange = useCallback((value) => {
    const point = exploitationPoints.find(p => p.point_number === Number(value));
    setSelectedPoint(point);
    const associatedValve = valves.find(valve => valve.valve_id === point.valve_id);
    setSelectedValve(associatedValve);
    fetchPointAudits(point.point_id);
  }, [exploitationPoints, valves, fetchPointAudits]);

  const handleDateRangeChange = useCallback((dates) => {
    setDateRange(dates);
    if (!dates || dates.length !== 2) {
      setSelectedDateRange(null);
      setDateRangeOptions([]);
    } else {
      const start = dates[0].format('YYYY-MM-DD');
      const end = dates[1].format('YYYY-MM-DD');
      setSelectedDateRange(`${start},${end}`);
    }
  }, []);

  const handleDateRangeOptionChange = useCallback((value) => {
    setSelectedDateRange(value);
  }, []);

  const mergedData = useMemo(() => {
    // Create a map to store merged data by timestamp
    const dataMap = new Map();
    
    // Add soil data to the map
    soilData.forEach(item => {
      const timestamp = item.reception_date;
      dataMap.set(timestamp, { ...item });
    });
    
    // Merge irrigation data into the map
    irrigationData.forEach(item => {
      const timestamp = item.reception_date;
      if (dataMap.has(timestamp)) {
        // Merge with existing soil data
        dataMap.set(timestamp, { ...dataMap.get(timestamp), ...item });
      } else {
        // Add as new entry
        dataMap.set(timestamp, { ...item });
      }
    });
    
    // Convert map back to array and sort by timestamp
    return Array.from(dataMap.values()).sort((a, b) => 
      new Date(a.reception_date) - new Date(b.reception_date)
    );
  }, [soilData, irrigationData]);

  return (
    <div className="p-4 md:p-6 w-full max-w-7xl mx-auto overflow-x-auto">
      <Title level={2} className="text-2xl md:text-3xl font-bold mb-6">
        Tableau de bord d'irrigation et de fertilisation
      </Title>

      <ParameterSelection
        exploitationPoints={exploitationPoints}
        selectedPoint={selectedPoint}
        dateRange={dateRange}
        dateRangeOptions={dateRangeOptions}
        selectedDateRange={selectedDateRange}
        onPointChange={handlePointChange}
        onDateRangeChange={handleDateRangeChange}
        onDateRangeOptionChange={handleDateRangeOptionChange}
        selectedValve={selectedValve}
      />

      {loading ? (
        <Loading />
      ) : error ? (
        <Alert message={error} type="error" />
      ) : mergedData.length > 0 ? (
        <div className="space-y-8">
          <IrrigationChartSection data={mergedData} selectedPoint={pointInfo} />
          <FertilizationChartSection data={mergedData} />
        </div>
      ) : (
        <Alert
          message="Aucune donnée disponible"
          description="Il n'y a pas de données disponibles pour la période et le point d'exploitation sélectionnés. Veuillez ajuster vos critères de sélection."
          type='info'
        />
      )}
    </div>
  );
}

export default SoilIrrigationMetricsGraphs;